import { SubscriptionCFA } from '@/account/types/subscription';
import { InsurancePlan, InsurancePlanTier, VehiclePlan } from '@/catalog/types/vehicle';
import HowMileageWorksDrawerDialog from '@/catalog/components/how-mileage-works-drawer-dialog';
import Spacer from '@/common/components/spacer';
import { Form } from 'react-router';
import MileagePackageHiddenInput from '@/catalog/components/mileage-package-hidden-input';
import { LoaderButton } from '@/common/components/loader-button';
import MileagePackageRadioGroup from '@/catalog/components/mileage-package-radio-group';
import usePDPSelectPlanSubmit from '@/catalog/hooks/use-pdp-select-plan-submit';
import StatusBanner from '@/common/components/status-banner';
import { useEffect, useState } from 'react';
import useRouteIsLoading from '@/util-react-router/hooks/use-route-is-loading';
import usePackageSummary from '@/catalog/hooks/use-package-summary';
import InsuranceDetailsDrawerDialog from '@/catalog/components/insurance-details-drawer-dialog';
import InsurancePackageHiddenInput from '@/catalog/components/insurance-package-hidden-input';
import PDPStickyBar from '@/catalog/components/pdp/proceed-to-schedule/pdp-sticky-bar';

interface SwapDetailsPaneProps {
  subscription: SubscriptionCFA;
  currentPlanMiles: number;
  currentInsurancePlanTier?: InsurancePlanTier;
  vehiclePlans: VehiclePlan[];
  insurancePlans?: InsurancePlan[];
  selectedMileagePlan?: number;
  mostPopularPlanMileageAmount?: string;
  planError?: string;
  shouldBreakoutInsurance?: boolean;
  enableInsuranceChange?: boolean;
  fullstoryPlanEventDelegate: () => void;
  fullstoryContinueEventDelegate: () => void;
}

export default function SwapDetailsPane({
  subscription,
  currentPlanMiles,
  currentInsurancePlanTier,
  vehiclePlans,
  insurancePlans,
  selectedMileagePlan,
  mostPopularPlanMileageAmount,
  planError,
  shouldBreakoutInsurance,
  enableInsuranceChange,
  fullstoryPlanEventDelegate,
  fullstoryContinueEventDelegate,
}: SwapDetailsPaneProps) {
  const { handleSelectPlan, selectedPlanId } = usePDPSelectPlanSubmit(
    vehiclePlans,
    selectedMileagePlan,
    fullstoryPlanEventDelegate
  );

  const [isSubmittingPlan, setIsSubmittingPlan] = useState(false);
  const isRunning = useRouteIsLoading();

  useEffect(() => {
    if (!isRunning) {
      setIsSubmittingPlan(false);
    }
  }, [isRunning]);

  const vehiclePlan = vehiclePlans.find(
    (plan) =>
      plan.miles_per_month ===
      (subscription.is_legacy_subscription ? selectedMileagePlan : currentPlanMiles)
  );

  const insurancePlan = insurancePlans?.find(
    (plan) => plan.tier === currentInsurancePlanTier
  );

  const packageSummary = usePackageSummary({
    selectedMileagePlanId: vehiclePlan ? String(vehiclePlan.id) : undefined,
    selectedInsurancePlanId: insurancePlan ? String(insurancePlan.id) : undefined,
    vehiclePlans,
    insurancePlans,
  });

  const handleMileagePlanChange = (event: string) => {
    setIsSubmittingPlan(true);
    handleSelectPlan(event);
  };

  return shouldBreakoutInsurance ? (
    <div className="px-8 md:px-7">
      {!subscription.is_legacy_subscription ? (
        <>
          <h2>Your monthly package</h2>
          <Spacer variant="height24" />
          <Form method="post">
            <div className="flex flex-row items-center justify-between">
              <h3 className="mt-[2px]">Your mileage plan</h3>
              <HowMileageWorksDrawerDialog
                showInsuranceSection={false}
                vehiclePlans={vehiclePlans}
              />
            </div>
            <Spacer variant="height16" />
            <MileagePackageHiddenInput
              currentPlanMiles={currentPlanMiles}
              vehiclePlans={vehiclePlans}
              variant="gray-rounded"
            />
            <Spacer variant="height24" />
            <div className="flex flex-row items-center justify-between">
              <h3 className="mt-[2px]">Your car protection</h3>
              <InsuranceDetailsDrawerDialog
                variant={'swap'}
                enableInsuranceChange={enableInsuranceChange}
                insurancePlans={insurancePlans}
              />
            </div>
            <Spacer variant="height16" />
            <InsurancePackageHiddenInput
              insurancePlans={insurancePlans}
              currentInsurancePlanTier={currentInsurancePlanTier}
            />
            <PDPStickyBar
              packageSummary={packageSummary}
              fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
            />
          </Form>
        </>
      ) : (
        <>
          <StatusBanner
            variant="info"
            message={{
              text: 'You must switch to a monthly plan in order to swap your Flexcar.',
            }}
          />
          <Spacer variant="height24" />
          <div className="flex flex-row items-center gap-[8px]">
            <h3 className="mt-[2px]">Select your monthly plan</h3>
            <HowMileageWorksDrawerDialog
              showInsuranceSection={false}
              vehiclePlans={vehiclePlans}
            />
          </div>
          <Spacer variant="height4" />
          <p className="body2">Insurance included. Change plans anytime.</p>
          <Spacer variant="height24" />
          <Form method="post" className="flex flex-col justify-center">
            <MileagePackageRadioGroup
              vehiclePlans={vehiclePlans}
              selectedPlanId={selectedPlanId || ''}
              mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
              onValueChange={(event) => handleMileagePlanChange(event)}
              error={planError}
              variant="no-radio"
            />
            <Spacer variant="height24" />
            <div className="flex flex-row items-center justify-between">
              <h3 className="mt-[2px]">Your car insurance</h3>
              <InsuranceDetailsDrawerDialog
                variant={'swap'}
                enableInsuranceChange={enableInsuranceChange}
                insurancePlans={insurancePlans}
              />
            </div>
            <Spacer variant="height16" />
            <InsurancePackageHiddenInput
              insurancePlans={insurancePlans}
              currentInsurancePlanTier={currentInsurancePlanTier}
            />
            <PDPStickyBar
              packageSummary={packageSummary}
              isLoading={isSubmittingPlan}
              fullstoryContinueEventDelegate={fullstoryContinueEventDelegate}
            />
          </Form>
        </>
      )}
    </div>
  ) : (
    <div className="px-8 md:px-7">
      {!subscription.is_legacy_subscription ? (
        <>
          <div className="flex flex-row items-center gap-[8px]">
            <h3 className="mt-[2px]">Your monthly plan</h3>
            <HowMileageWorksDrawerDialog vehiclePlans={vehiclePlans} />
          </div>

          <Spacer variant="height24" />
          <Form method="post">
            <MileagePackageHiddenInput
              currentPlanMiles={currentPlanMiles}
              vehiclePlans={vehiclePlans}
            />
            <Spacer variant="height24" />
            <LoaderButton type="submit" name="intent" value="plan" className="w-full">
              Continue to schedule
            </LoaderButton>
          </Form>
        </>
      ) : (
        <>
          <StatusBanner
            variant="info"
            message={{
              text: 'You must switch to a monthly plan in order to swap your Flexcar.',
            }}
          />
          <Spacer variant="height24" />
          <div className="flex flex-row items-center gap-[8px]">
            <h3 className="mt-[2px]">Select your monthly plan</h3>
            <HowMileageWorksDrawerDialog vehiclePlans={vehiclePlans} />
          </div>
          <Spacer variant="height4" />
          <p className="body2">Insurance included. Change plans anytime.</p>
          <Spacer variant="height24" />
          <Form method="post" className="flex flex-col justify-center">
            <MileagePackageRadioGroup
              vehiclePlans={vehiclePlans}
              selectedPlanId={selectedPlanId || ''}
              mostPopularPlanMileageAmount={mostPopularPlanMileageAmount}
              onValueChange={(event) => handleSelectPlan(event)}
              error={planError}
            />

            <Spacer variant="height24" />
            <LoaderButton type="submit" name="intent" value="plan" className="w-full">
              Continue to schedule
            </LoaderButton>
          </Form>
        </>
      )}
    </div>
  );
}
